import * as React from 'react';
import { observable, action } from 'mobx';
import { observer } from 'mobx-react';
import { Dropdown, IDropdown, IDropdownOption } from 'office-ui-fabric-react';

import { NotificationHandler } from '@app/Components/ToastNotification';
import { ContractTypeModel, ContractType, PersonContractViewModel, PersonFunctionViewModel } from '@app/Models';
import { PartTimeTzvBlock } from '@app/Pages/PersonInfo/Components/PartTimeTzvBlock';
import { EnumHelpers } from '@app/Services/EnumHelpers';

import { modalService, ModalButtonType } from '../Modal';
import { TextCell } from './TextCell';
import { ViewMode } from './ViewMode';

type OnChangeContractCallback = (contract: ContractModel) => Promise<boolean>;

type ContractModel = PersonContractViewModel;

type InlineContractDropdownCellProps<T extends ContractModel> = {
    value: ContractType;
    row: T;
    fieldKey: keyof T;
    onChangeHandler: OnChangeContractCallback;
    disabled?: boolean;
    isReadOnly?: boolean;
    personFunctions: PersonFunctionViewModel[];
};

@observer
export class InlineContractDropdownCell<T extends ContractModel> extends React.Component<InlineContractDropdownCellProps<T>, {}> {
    store: InlineContractDropdownCellStore<T>;

    constructor(props: InlineContractDropdownCellProps<T>) {
        super(props);
        this.store = new InlineContractDropdownCellStore(props);
    }

    componentDidUpdate(prevProps: InlineContractDropdownCellProps<T>) {
        if (this.props.row.id !== prevProps.row.id || this.props.value !== prevProps.value
            || this.props.row.partTimePercentage !== prevProps.row.partTimePercentage
            || this.props.row.dateFrom !== prevProps.row.dateFrom
            || this.props.row.dateTo !== prevProps.row.dateTo
            || this.props.personFunctions !== prevProps.personFunctions) {
            this.store.updateProps(this.props);
        }
    }

    render() {
        const { mode, toEditMode, onChange, ref, onMouseLeave, type } = this.store;
        const { disabled } = this.props;
        const nodes: JSX.Element[] = [];
        if (mode === ViewMode.view) {
            return <TextCell onClick={toEditMode} value={type as string} />;
        } else {
            nodes.push(
                <Dropdown key="dropdown"
                    options={EnumHelpers.contractTypeOptions()}
                    onChange={onChange}
                    componentRef={ref}
                    onDismiss={onMouseLeave}
                    selectedKey={type as string}
                    disabled={disabled}
                />
            );
        }
        return nodes;
    }

    static renderPartTimeInfo(row: PersonContractViewModel | null) {
        if (!row) return [];
        const result: JSX.Element[] = [];

        if (row.typeModel === ContractTypeModel.TZV_Month) {
            result.push(
                <ul className="list-unstyled text-left mb-0">
                    <li>Contract: {row.partTimeMonthContractDefinition}</li>
                </ul>
            );
        }
        if (row.typeModel === ContractTypeModel.TZV_Day) {
            result.push(
                <ul className="list-unstyled text-left mb-0">
                    <li>Contract: {row.partTimeDayContractDefinition}</li>
                    <li>Split Type: {row.partTimeDaySplitType}</li>
                </ul>
            );
        }
        if (row.typeModel === ContractTypeModel.TZV_VariableBlock) {
            result.push(
                <ul className="list-unstyled text-left mb-0">
                    <li>Block Length: {row.partTimeVariableBlockLength}</li>
                    <li>Blocks Per Year: {row.partTimeVariableBlockPerYear}</li>
                </ul>
            );
        }
        if (row.typeModel === ContractTypeModel.TZV_VariableDay || row.typeModel === ContractTypeModel.TZV_VariableDay_LhSff) {
            result.push(
                <ul className="list-unstyled text-left mb-0">
                    <li>Block Length: {row.partTimeVariableDayBlockLength}</li>
                    <li>Split Type: {row.partTimeVariableDaySplitType}</li>
                </ul>
            );
        }
        if (row.typeModel === ContractTypeModel.TZV_FixBlock) {
            result.push(
                <ul className="list-unstyled text-left mb-0">
                    <li>Days: {row.partTimeFixBlockDays}</li>
                    <li>Frequency: {row.partTimeFixBlockFrequency}</li>
                    <li>Begin Day: {row.partTimeFixBlockBeginDay}</li>
                    <li>Begin Month: {row.partTimeFixBlockBeginMonth}</li>
                </ul>
            );
        }
        if (row.typeModel === ContractTypeModel.TZV_FixDay || row.typeModel === ContractTypeModel.TZV_FixDay_Sh || row.typeModel === ContractTypeModel.TZV_FixDay_Mff) {
            result.push(
                <ul className="list-unstyled text-left mb-0">
                    {row.partTimeDayContractDefinition && <li>Contract: {row.partTimeDayContractDefinition}</li>}
                    {row.typeModel === ContractTypeModel.TZV_FixDay_Mff && <li>Week: {row.partTimeFixDaysStartWeekPerContract}</li>}
                    <li>Days Per Week: {row.partTimeFixDaysPerWeek}</li>
                    <li>Day 1: {row.partTimeFixDayFirstDay}</li>
                    {row.partTimeFixDaySecondDay && <li>Day 2: {row.partTimeFixDaySecondDay}</li>}
                    {row.partTimeDaySplitType && <li>Split Type: {row.partTimeDaySplitType}</li>}
                </ul>
            );
        }
        if (row.typeModel === ContractTypeModel.TZV_Spez) {
            result.push(
                <ul className="list-unstyled text-left mb-0">
                    <li>TZV Days Per Year: {row.partTimeSpecialDaysPerYear}</li>
                    <li>VacEntitlement: {(Math.round(Number(row.partTimeSpecialVacEntitlement) * 100000) / 1000).toString()}%</li>
                </ul>
            );
        }
        if (row.combinationVariableDayBlockLength && row.combinationVariableDaySplitType) {
            result.push(
                <ul className="list-unstyled text-left mb-0">
                    <li>Block Length: {row.combinationVariableDayBlockLength}</li>
                    <li>Split Type: {row.combinationVariableDaySplitType}</li>
                </ul>
            );
        }

        return result;
    }
}

class InlineContractDropdownCellStore<T extends ContractModel> {
    @observable mode: string = ViewMode.view;
    @observable type: ContractType;
    @observable _initType: ContractType;
    ref: React.RefObject<IDropdown> = React.createRef();
    private _currentProps: InlineContractDropdownCellProps<T>;
    prevType: ContractType;

    constructor(props: InlineContractDropdownCellProps<T>) {
        this.updateProps(props);
    }

    @action
    toEditMode = () => {
        if (this._currentProps.isReadOnly || this._currentProps.disabled) return;
        this.mode = ViewMode.edit;
        setTimeout(() => {
            this.ref.current && this.ref.current.focus(true);
        }, 0);
    };

    @action
    onMouseLeave = () => {
        this.mode = ViewMode.view;
    };

    @action
    onChange = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
        this.mode = ViewMode.view;
        if (option) {
            this.prevType = this.type;
            this.type = option.key as ContractType;
        }
        this._initType = this.type;
        const row = this._currentProps.row as unknown as { [key: string]: string };
        row[this._currentProps.fieldKey as string] = this.type as string;
        if (this.type === ContractType.Fix && !this._currentProps.row.partTimePercentage) {
            this._currentProps.row.partTimePercentage = 1;
            NotificationHandler.showWarning('Employment percentage can not be empty for Fix type ', 'Employment percentage set to 100%');
        }

        if (this.type === ContractType.Tzv) {
            this._changePartTimeTzvContract();
        } else {
            this._handleInfluenceTypeOnSubType();
            this._currentProps.onChangeHandler(this._currentProps.row);
        }
    };

    @action
    updateProps(props: InlineContractDropdownCellProps<T>) {
        this._initType = props.value;
        this._currentProps = { ...props };
        this.type = props.value;
    }

    _handleInfluenceTypeOnSubType() {
        const typeModel = this._currentProps.row.typeModel;
        if ((this.type === ContractType.Fix || this.type === ContractType.Fse || this.type === ContractType.None)
            && typeModel !== ContractTypeModel.None) {
            const newValue = ContractTypeModel.None;
            this._currentProps.row.typeModel = newValue as keyof ContractTypeModel;
            NotificationHandler.showWarning(`Employment Type Model can have only ${newValue} for ${this.type} `, `Employment Type Model set to ${newValue}`);
        } else if (this.type === ContractType.Freelance && typeModel !== ContractTypeModel.FL_S && typeModel !== ContractTypeModel.FL_A
            && typeModel !== ContractTypeModel.FL_C && typeModel !== ContractTypeModel.FL_B) {
            const newValue = ContractTypeModel.FL_A;
            this._currentProps.row.typeModel = newValue as keyof ContractTypeModel;
            NotificationHandler.showWarning(`Employment Type Model can have ${ContractTypeModel.FL_A}, ${ContractTypeModel.FL_B}, ${ContractTypeModel.FL_C}, ${ContractTypeModel.FL_S} for ${this.type} employment type`, `Employment Type Model set to ${newValue}`);
        }
    }

    @action.bound
    private async _changePartTimeTzvContract() {
        if (!this._currentProps.personFunctions) {
            return;
        }

        const { button } = await modalService.show(PartTimeTzvBlock, {
            contract: this._currentProps.row,
            personFunctions: this._currentProps.personFunctions,
            onSave: (c) => this._currentProps.onChangeHandler(c as PersonContractViewModel),
        });

        if (button === ModalButtonType.Cancel) {
            this.type = this.prevType;
            this._initType = this.type;
            const row = this._currentProps.row as unknown as { [key: string]: string };
            row[this._currentProps.fieldKey as string] = this.type as string;
            if (this.type === ContractType.Fix && !this._currentProps.row.partTimePercentage) {
                this._currentProps.row.partTimePercentage = 1;
                NotificationHandler.showWarning('Employment percentage can not be empty for Fix type ', 'Employment percentage set to 100%');
            }
            this._handleInfluenceTypeOnSubType();
        }
    }

}
